/**
 * Structure colour proposal card (the runtime HITL gate) for
 * `set_structure_color`. Pure presentational: it shows the structure and
 * its current and proposed colour as swatches; nothing changes until the
 * clinician presses Apply, which uses the same command as the structure
 * list's colour picker. Frame, status chip and Apply/Reject live in
 * {@link ProposalCardShell}.
 */
import { Box, Typography } from '@mui/material';
import ProposalCardShell, {
    ProposalSummaryBox,
    type ProposalStatus,
} from './ProposalCardShell';

export interface StructureColorProposalCardProps {
    readonly targetName: string;
    readonly targetRoiNumber: number;
    /** #RRGGBB, or empty when unknown. */
    readonly fromColor: string;
    /** #RRGGBB */
    readonly toColor: string;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

function Swatch({ color, label }: { color: string; label: string }) {
    return (
        <Box
            component="span"
            role="img"
            aria-label={`${label} ${color}`}
            sx={(t) => ({
                display: 'inline-block',
                width: 12,
                height: 12,
                mx: 0.5,
                verticalAlign: 'middle',
                borderRadius: '2px',
                border: `1px solid ${t.palette.divider}`,
                backgroundColor: color,
            })}
        />
    );
}

export default function StructureColorProposalCard({
    targetName,
    targetRoiNumber,
    fromColor,
    toColor,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: StructureColorProposalCardProps) {
    return (
        <ProposalCardShell
            title="Structure colour"
            ariaLabel="Structure colour proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <ProposalSummaryBox>
                “{targetName}” #{targetRoiNumber}:
                {fromColor ? (
                    <>
                        <Swatch color={fromColor} label="current" />
                        {fromColor} →
                    </>
                ) : (
                    ' →'
                )}
                <Swatch color={toColor} label="new" />
                <strong>{toColor}</strong>
            </ProposalSummaryBox>
            <Typography variant="caption" color="text.secondary">
                Same as the colour picker. The colour is saved with the
                structure set.
            </Typography>
        </ProposalCardShell>
    );
}
